import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workOrderApi } from '../services/workOrderApi';
import { accountingSyncKeys } from './useAccountingSync';
import { creditNoteKeys } from './useCreditNotes';
import { paymentKeys } from './usePayments';
import type {
  CreateWorkOrderRequest,
  ResolveDisputeRequest,
  UpdateWorkOrderRequest,
  WorkOrderListFilters,
} from '../types/service.types';

export const workOrderKeys = {
  all: ['work-orders'] as const,
  lists: () => [...workOrderKeys.all, 'list'] as const,
  list: (filters: WorkOrderListFilters = {}) => [...workOrderKeys.lists(), filters] as const,
  details: () => [...workOrderKeys.all, 'detail'] as const,
  detail: (id: string) => [...workOrderKeys.details(), id] as const,
};

/**
 * Every mutation in EVERY Service hook file (this one and its four
 * siblings) calls this in `onSuccess`. A job/part/charge/payment change
 * alters the parent work order's embedded tree and its totals, so the WO
 * detail is always the thing to invalidate — never only the child's own key.
 */
export function useInvalidateWorkOrder() {
  const queryClient = useQueryClient();
  return (id: string) => {
    queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
  };
}

/**
 * Caller must pass an already-memoised `filters` object — a fresh object
 * literal on every render changes the query key every render and refetches
 * forever (the caution in `modules/crm/hooks/useCustomers.ts`).
 */
export function useWorkOrders(filters: WorkOrderListFilters = {}) {
  return useQuery({
    queryKey: workOrderKeys.list(filters),
    queryFn: () => workOrderApi.list(filters),
  });
}

/**
 * D9-09's freshness policy for a live screen: `staleTime: 30_000` means
 * ordinary navigation back to this detail rarely refetches on its own, and
 * `refetchOnWindowFocus: true` means a second advisor's change to the same
 * work order still appears the moment this tab regains focus.
 */
export function useWorkOrder(id: string) {
  return useQuery({
    queryKey: workOrderKeys.detail(id),
    queryFn: () => workOrderApi.get(id),
    enabled: !!id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkOrderRequest) => workOrderApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
    },
  });
}

export function useUpdateWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkOrderRequest }) =>
      workOrderApi.update(id, data),
    onSuccess: (_, variables) => invalidateWorkOrder(variables.id),
  });
}

// ---- One mutation hook per WO verb ----

export function useConfirmWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: (id: string) => workOrderApi.confirm(id),
    onSuccess: (_, id) => invalidateWorkOrder(id),
  });
}

export function useUnconfirmWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: (id: string) => workOrderApi.unconfirm(id),
    onSuccess: (_, id) => invalidateWorkOrder(id),
  });
}

export function useCheckInWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: (id: string) => workOrderApi.checkIn(id),
    onSuccess: (_, id) => invalidateWorkOrder(id),
  });
}

export function usePauseWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      workOrderApi.pause(id, reason),
    onSuccess: (_, variables) => invalidateWorkOrder(variables.id),
  });
}

export function useResumeWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: (id: string) => workOrderApi.resume(id),
    onSuccess: (_, id) => invalidateWorkOrder(id),
  });
}

export function useReadyForCollection() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: (id: string) => workOrderApi.readyForCollection(id),
    onSuccess: (_, id) => invalidateWorkOrder(id),
  });
}

export function useCancelWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      workOrderApi.cancel(id, reason),
    onSuccess: (_, variables) => invalidateWorkOrder(variables.id),
  });
}

export function useVoidWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      workOrderApi.voidWorkOrder(id, reason),
    onSuccess: (_, variables) => invalidateWorkOrder(variables.id),
  });
}

export function useInvoiceWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: ({ id, taxCents }: { id: string; taxCents?: number }) =>
      workOrderApi.invoice(id, taxCents),
    onSuccess: (_, variables) => invalidateWorkOrder(variables.id),
  });
}

/**
 * 16-REVIEW WR-02 — reverse-and-reissue mints a correction note and, on a
 * `sent` / `partial` original, applies it (a `house_account_drawdown`
 * payment); resolve-dispute `closed` applies a note the same way. Both
 * therefore refresh the original's credit-note and payment lists and the
 * customer-scoped credit lists, exactly as `useCreditNotes` /
 * `usePayments` do for the same effects.
 */
function useInvalidateCreditAndPayments() {
  const queryClient = useQueryClient();
  return (workOrderId: string) => {
    queryClient.invalidateQueries({ queryKey: creditNoteKeys.list(workOrderId) });
    queryClient.invalidateQueries({ queryKey: creditNoteKeys.customerLists() });
    queryClient.invalidateQueries({ queryKey: paymentKeys.list(workOrderId) });
  };
}

/** D16-25: 201 returns the NEW work order. Also refreshes the finance sync surface (T-16-53). */
export function useReverseAndReissue() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const invalidateCreditAndPayments = useInvalidateCreditAndPayments();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      workOrderApi.reverseAndReissue(id, reason),
    onSuccess: (created, variables) => {
      invalidateWorkOrder(variables.id);
      invalidateCreditAndPayments(variables.id);
      if (created.id) {
        queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(created.id) });
      }
      queryClient.invalidateQueries({ queryKey: accountingSyncKeys.all });
    },
  });
}

export function useUnlockInvoice() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => workOrderApi.unlock(id, reason),
    onSuccess: (_, variables) => {
      invalidateWorkOrder(variables.id);
      queryClient.invalidateQueries({ queryKey: accountingSyncKeys.all });
    },
  });
}

export function useDisputeWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => workOrderApi.dispute(id, reason),
    onSuccess: (_, variables) => invalidateWorkOrder(variables.id),
  });
}

export function useResolveDispute() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const invalidateCreditAndPayments = useInvalidateCreditAndPayments();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveDisputeRequest }) =>
      workOrderApi.resolveDispute(id, body),
    onSuccess: (_, variables) => {
      invalidateWorkOrder(variables.id);
      invalidateCreditAndPayments(variables.id);
    },
  });
}

export function useReopenWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => workOrderApi.reopen(id, reason),
    onSuccess: (_, variables) => invalidateWorkOrder(variables.id),
  });
}

export function useCloseWorkOrder() {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: (id: string) => workOrderApi.close(id),
    onSuccess: (_, id) => invalidateWorkOrder(id),
  });
}
